import { message as $message } from 'antd'
import { history } from 'umi'
import Qs from 'qs'
import omit from 'lodash/omit'
import { exchangeToken } from '@/modules/globals/utils/host'
import { captureException } from '@/utils/track'
import { asycGetErpToken } from '@/utils/public'
import { getToken, setToken, resolveToken } from './core'
import { readQueryFromUrl } from './utils/url'
import { checkIsTopFrame } from './utils/frame'
import { getTokenByTicket, afterLogin, syncApplyCooperationNum } from '@/services/login'

const APP_CONTAINER_ENUM = {
    erp: 'erp',
    epaas: 'epaas',
    gyl: 'gyl',
}

/**
 * 顶层容器监听token查询
 */
const listenTokenQuery = () => {
    if (!window.jmx) {
        return Promise.reject(new Error('jmx未初始化'))
    }

    // 查询gyl token
    const getGylToken = ({ token }) => {
        const gylToken = getToken()

        window.jmx.emit(
            'frame.common.getGylToken-Cb',
            {
                gylToken,
            },
            {
                token,
            },
        )
    }

    // 查询erp token
    const getErpToken = async ({ token }) => {
        const erpToken = await asycGetErpToken()

        window.jmx.emit(
            'frame.common.getErpToken-Cb',
            {
                erpToken,
            },
            {
                token,
            },
        )
    }

    window.jmx.on('frame.common.getGylToken', getGylToken)
    window.jmx.on('frame.common.getErpToken', getErpToken)
}

const MAX_TIMEOUT = 2000

/**
 * 登陆后完成需要做一些事
 * 老代码是短token换完后调用，切成postMessage需要判断容器，供应链容器中不需要执行
 * t、eTicket一定是内嵌再erp，postMessage方式不一定
 */
const afterLoginAction = ({ ffrom }) => {
    return new Promise(resolve => {
        const timer = setTimeout(() => {
            // 注意：此处抛出的错误不会被捕获，window.onerror可以监听到，sentry应该可以有记录（老逻辑如此）
            throw new Error('接口超时')
        }, MAX_TIMEOUT)

        // 和后端确认afterLogin, syncApplyCooperationNum两接口调用保持原样
        afterLogin({
            fromSource: ffrom,
        })
            .then(() => {
                clearTimeout(timer)
                syncApplyCooperationNum() //  不需要等 @花葱
                resolve()
            })
            .catch(e => {
                console.error(e)
                resolve()
            })
    })
}

/**
 * 从url上获取token
 */
const receiveTokenFromUrl = async () => {
    console.time('receiveTokenFromUrl')

    try {
        const query = readQueryFromUrl()

        // url上直接携带token（废弃！方式保留，非特殊情况禁用!!!）
        if (query.token) {
            console.log('receiveTokenFromUrl:', query)
            setToken({ access_token: query.token })
            clearRelatedTokenUrlParams(query)

            console.timeEnd('receiveTokenFromUrl')
            return
        }

        // 【ERP】短token换长token
        if (query.eTicket || query.t) {
            const topFrameEnv = await window.jst_core.env.getEnv() // 'erp'|'epaas'|'gyl'|''

            console.log('receiveTokenFromUrl:', query)
            const isRapid = [APP_CONTAINER_ENUM.erp, APP_CONTAINER_ENUM.epaas].includes(topFrameEnv)

            const res = await exchangeToken({
                eTicket: query.eTicket,
                t: query.t,
                isRapid,
                ffrom: query.ffrom,
                serviceKey: query.serviceKey,
            })

            // 转换成功
            if (res?.access_token) {
                // 注意此处setToken的时候不resolve，checkTokenIsReady会阻塞渲染
                setToken({ access_token: res.access_token, _notResolveToken: true })

                // 注意：需要塞页面渲染
                await afterLoginAction(query)

                resolveToken()

                console.timeEnd('receiveTokenFromUrl')
                return
            } else {
                // 转换失败
            }
        }

        // 【GYL】短token换长token
        if (query._gt) {
            const res = await getTokenByTicket({
                ticket: query._gt,
            })

            if (res?.success) {
                setToken({ access_token: res.data })
                clearRelatedTokenUrlParams(query)
                console.timeEnd('receiveTokenFromTopFrame')
                return
            } else {
                $message.error(res.message)
            }
        }
    } catch (error) {
        console.error('catchError', error)

        captureException(`token接收错误`, {
            anchor: `receiveAccessTokenFromUrl`,
            mustNotify: true,
            extra: error,
        })
    }

    resolveToken()

    console.timeEnd('receiveTokenFromUrl')
}

/**
 * 接收token
 */
const receiveTokenFromTopFrame = async () => {
    console.time('receiveTokenFromTopFrame')

    // e2e 测试兼容
    if (window.Cypress) {
        resolveToken()
        console.timeEnd('receiveTokenFromTopFrame')
        return
    }

    try {
        console.time('receiveTokenFromTopFrame.readQueryFromUrl')
        const query = readQueryFromUrl()
        console.timeEnd('receiveTokenFromTopFrame.readQueryFromUrl')

        // url上直接携带token（废弃！方式保留，非特殊情况禁用!!!）
        if (query.token) {
            console.log('receiveAccessToken:', query)
            setToken({ access_token: query.token })

            console.timeEnd('receiveTokenFromTopFrame')
            return
        }

        // 【ERP】短token换长token
        if (query.eTicket || query.t) {
            const topFrameEnv = await window.jst_core.env.getEnv() // 'erp'|'epaas'|'gyl'|''

            const isRapid = [APP_CONTAINER_ENUM.erp, APP_CONTAINER_ENUM.epaas].includes(topFrameEnv)

            const res = await exchangeToken({
                eTicket: query.eTicket,
                t: query.t,
                isRapid,
                ffrom: query.ffrom,
                serviceKey: query.serviceKey,
            })

            if (res?.access_token) {
                // 注意此处setToken的时候不resolve，checkTokenIsReady会阻塞渲染
                setToken({ access_token: res.access_token, _notResolveToken: true })

                // 注意：需要塞页面渲染
                await afterLoginAction(query)

                resolveToken()

                console.timeEnd('receiveTokenFromTopFrame')
                return
            } else {
                // 转换失败
            }
        }

        // 【GYL】短token换长token
        if (query._gt) {
            const res = await getTokenByTicket({
                ticket: query._gt,
            })

            if (res?.success) {
                setToken({ access_token: res.data })
                console.timeEnd('receiveTokenFromTopFrame')
                return
            } else {
                $message.error(res.message)
            }
        }

        console.time('receiveTokenFromTopFrame.getEnv')
        const topFrameEnv = await window.jst_core.env.getEnv() // 'erp'|'epaas'|'gyl'|''
        console.timeEnd('receiveTokenFromTopFrame.getEnv')

        // 注意：erp容器不再更新，短token不去除并且不支持通过postMessage查询token！！！所以erp不走下面的逻辑
        if (![APP_CONTAINER_ENUM.erp].includes(topFrameEnv)) {
            // 若容器端监听代码还未上线，但是内嵌页又不需要登陆（url上未携带token），会出问题
            console.time('receiveTokenFromTopFrame.queryTokenFromTopFrame')
            const res = await queryTokenFromTopFrame()
            console.timeEnd('receiveTokenFromTopFrame.queryTokenFromTopFrame')

            if (res?.gylToken) {
                console.log('receiveAccessToken:', res)
                setToken({ access_token: res.gylToken, _notResolveToken: true })

                const isGyl = topFrameEnv === APP_CONTAINER_ENUM.gyl

                // 非gyl才执行afterLogin逻辑
                if (!isGyl) {
                    // 注意：需要塞页面渲染
                    console.time('receiveTokenFromTopFrame.afterLoginAction')
                    await afterLoginAction(query)
                    console.timeEnd('receiveTokenFromTopFrame.afterLoginAction')
                }

                console.time('receiveTokenFromTopFrame.resolveToken')
                resolveToken()
                console.timeEnd('receiveTokenFromTopFrame.resolveToken')

                console.timeEnd('receiveTokenFromTopFrame')
                return
            }
        }
    } catch (error) {
        console.error('token接收错误 receiveTokenFromTopFrame', error)
    }

    resolveToken()

    console.timeEnd('receiveTokenFromTopFrame')
}

const MAX_TIME_OUT = 2000 // 最大超时时间2s，防止出意外死在这里

/**
 * 从顶层框架获取token
 */
const queryTokenFromTopFrame = () => {
    return new Promise((resolve, reject) => {
        if (!window.jmx) {
            reject(new Error('jmx未初始化'))
            return
        }

        // 防止出意外
        const timer = setTimeout(() => {
            reject(new Error('queryTokenFromTopFrame 超时'))
        }, MAX_TIME_OUT)

        console.time('queryTokenFromTopFrame')

        const uToken = window.jmx.createToken()

        const getGylTokenResult = res => {
            console.log('queryTokenFromToFrame:', res)

            timer && clearTimeout(timer)

            resolve({
                gylToken: res.gylToken,
            })

            window.jmx.off('frame.common.getGylToken-Cb', getGylTokenResult, { token: uToken })

            console.timeEnd('queryTokenFromTopFrame')
        }

        window.jmx.on('frame.common.getGylToken-Cb', getGylTokenResult, { token: uToken })
        window.jmx.emit('frame.common.getGylToken', { token: uToken })
    })
}

/**
 * 清除url中query相关的参数，注意只清除主应用的
 */
const clearRelatedTokenUrlParams = query => {
    // 需要移除的key列表
    const deleteKeys = ['token', '_gt']

    const newQuery = omit(query, deleteKeys)

    const newLocation = {
        ...location,
        query: newQuery,
        search: Qs.stringify(newQuery, { addQueryPrefix: true }),
    }

    window.history.replaceState(null, '', history.createHref(newLocation)) // 直接使用原生api删除token，不会触发重渲染

    deleteKeys.forEach(fieldKey => {
        delete history.location.query[fieldKey] // 删除umi history中的参数，（部分地方读取了整个query作为页面的筛选参数）
    })
}

/**
 * 出初始化token获取
 */
export default () => {
    const isTopFrame = checkIsTopFrame()

    // 子应用发送消息给顶层容器获取token
    if (isTopFrame) {
        // 顶层监听
        listenTokenQuery()
        return receiveTokenFromUrl()
    }

    // 子应用接收
    return receiveTokenFromTopFrame()
}
